import { css } from '@linaria/core'
import { styled } from '@linaria/react'
import React, { useEffect, useState } from 'react'
import Text from '@components/Text'
import { themeDefault } from 'src/theme-revamp'
import { useDispatch } from 'react-redux'
import AdvisorMessageBox from './components/AdvisorMessageBox'
import Listing from '@components/_Revamp/Listing'
import Button from '@components/_Revamp/Button'
import ContactUs from '@components/_Revamp/ContactUs'
import { useAvivaSelector } from '@redux/hook'
import { get, isEmpty } from 'lodash'
import { Helpers } from '@utils/helpers'
import { SQSPocketResponse } from '@models/MLTC/SQSPocketRequest.model';
import { GAAviva, MLTC_R3_JOURNEY_NAME } from '@utils/gaAvivaR3'
import { useHistory } from 'react-router-dom'
import { updatePlanSelection, updateSQSQuotation } from '@redux/slices/MLTC_R3/step2Slice'
import { completeStep } from '@redux/slices/MLTC_R3/step5Slice'
import { mask } from '@utils/mask'
import { DISCLAIMER_TEXT } from '@static/content'

export interface ThankYouProps { }

export interface EditBtnProps {
  onClick?: (e: any) => void
}

const ThankYou = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [campaignCode, setCampaignCode] = useState<string>('MLO1');
  const [summaries, setSummaries] = useState<any>(undefined);
  const [planSummary, setPlanSummary] = useState<any>([]);

  const policyNo = useAvivaSelector(state => state.mltcR3Step5.policyNo);
  const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);  
  const basicDetails = useAvivaSelector(state => state.mltcR3Step1.basicDetails);
  const age = basicDetails?.dob
    ? Helpers.getAge(
        new Date(
          `${basicDetails.dob.month}/${basicDetails.dob.day}/${basicDetails.dob.year}`,
        ),
      )
    : null; 
  const isEBLTC = campaignDetail?.code === "EBLTC" && campaignDetail?.status !== "EXPIRED" && age !== null && (age >= 31 && age <= 40);
  const quoteRawResponse = useAvivaSelector(state => state.mltcR3Step2.quoteRawResponse);
  const qrLTCDiscountDescription = useAvivaSelector(state => state.mltcR3Step5.qrLTCDiscountDescription);
  const premiumPaymentMethod = useAvivaSelector(state => state.mltcR3Step5.premiumPaymentMethod);
  const agentDetailResponseRaw = useAvivaSelector(state => state.mltcR3Step5.agentDetailResponseRaw);
  // QB-AA-Tracking fix: "" when the customer's campaign type skipped Agency.tsx entirely
  // (e.g. straight-to-payment QR/CSTOPUP/GIO flows never set this); "NO_AGENT" when they
  // were offered an adviser and declined. Both count as "no financial adviser".
  const advisorSelection = useAvivaSelector(state => state.mltcR3Step6.advisorSelection);
  const gioDeclaration = useAvivaSelector(state => state.mltcR3Step2.gioDeclaration);
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [aaTracking,setAATracking] = useState<string[]>([]);
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);
  const [disclaimerText, setDisclaimerText] = useState<string>("");

  const goToMainWebsite = () =>{
    let avivaWebsiteUrl = get(window, 'AVIVA_VARIABLE_CONFIG.avivaWebsiteUrl');
    window.open(avivaWebsiteUrl,"_self");
  }

  const updatePlanSummary = (planSummary: any, firstYearDiscountEligible: boolean = false) => {
    let paymentMethodString = isEBLTC ? (      
      <React.Fragment>
        <div className={labelCss}> First Year</div>
        {`CPF Medisave - S$ 1.00`}
        {premiumPaymentMethod?.cpfFirstYearCreditCardAmount > 0 && <br />}
        {premiumPaymentMethod?.cpfFirstYearCreditCardAmount > 0 &&
          `Credit Card - ${Helpers.toCurrency(
            premiumPaymentMethod?.cpfFirstYearCreditCardAmount,
          )}`}

          <div className={labelSubCss}> Subsequent</div>
        {premiumPaymentMethod?.cpfMediSaveAmount > 0 &&
          `CPF Medisave - ${Helpers.toCurrency(
            premiumPaymentMethod?.cpfMediSaveAmount,
          )}`}
        {premiumPaymentMethod?.cpfMediSaveAmount > 0 &&
          premiumPaymentMethod?.creditCardAmount > 0 && <br />}
        {premiumPaymentMethod?.creditCardAmount > 0 &&
          `Credit Card - ${Helpers.toCurrency(
            premiumPaymentMethod?.creditCardAmount,
          )}`}
      </React.Fragment>
    ) : (
      <React.Fragment>
        {premiumPaymentMethod?.cpfMediSaveAmount > 0 &&
          `CPF Medisave - ${Helpers.toCurrency(
            premiumPaymentMethod?.cpfMediSaveAmount,
          )}`}
        {premiumPaymentMethod?.cpfMediSaveAmount > 0 &&
          premiumPaymentMethod?.creditCardAmount > 0 && <br />}
        {premiumPaymentMethod?.creditCardAmount > 0 &&
          `Credit Card - ${Helpers.toCurrency(
            premiumPaymentMethod?.creditCardAmount,
          )}`}
      </React.Fragment>
    );

    const EBCampaignDiscount = (
        <React.Fragment>
          {`- ${qrLTCDiscountDescription}`}
          {<br />}
          {`-S$599 Discount`}
        </React.Fragment>
      );

    const summarySources = [
      { heading: { text: 'Policy Number', value: `${policyNo ? policyNo : '-'}` } },
      { heading: { text: 'Plan', value: `${planSummary[1]?.value ? planSummary[1]?.value : '-'}` } },
      { heading: { text: 'Monthly Benefit Payout', value: `${planSummary[2]?.value ? planSummary[2]?.value : '-'}` } },
      { heading: { text: 'Plan Structure', value: `${planSummary[3]?.value ? planSummary[3]?.value : '-'}` } },
      { heading: { text: 'Policy Term', value: `${planSummary[4]?.value ? planSummary[4]?.value : '-'}` } },
      { heading: { text: 'Premium Term', value: `${planSummary[5]?.value ? planSummary[5]?.value : '-'}` } },
      ...(gioDeclaration === 'GIO'
      ? [{ heading: { text: 'Underwriting Campaign', value: 'Eligible' }}]
      : []),
      { heading: { text: `${firstYearDiscountEligible ? 'Premium Amount - 1st Year*': 'Premium Amount'}`, value: `${planSummary[6]?.value ? planSummary[6]?.value : '-'}` } },
      ...(isEBLTC ? [{ heading: { text: 'Singlife EB Campaign', value: EBCampaignDiscount }}] : []),
      { heading: { text: 'Premium Payment Method', value: paymentMethodString } }
    ];
    if (firstYearDiscountEligible && campaignDetail?.partner !== "DDM18"){
      setShowDisclaimer(true);
      setDisclaimerText(DISCLAIMER_TEXT)
    }
    if(campaignDetail?.partner === "DDM18"){
      summarySources[6].heading.text = summarySources[6].heading.text.replace('*',"")
    }
    setPlanSummary(summarySources);
  }

  const detectScreenWidth = () => {
    setScreenSize(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener('resize', detectScreenWidth);
    return () => {
      window.removeEventListener('resize', detectScreenWidth);
    };
  }, [screenSize]);

  const trackOnLoad = (formName: string): void => {
    if(!aaTracking.includes(formName)){
      setAATracking([...aaTracking,formName]);
      const GA = GAAviva.getInstance();
      let { form, acquisition, page } = GA.digitalData;
      form.name = formName;

      // QB-AA-Tracking fix (checkout revenue):
      // `acquisition.insurance.premium` used to be left as whatever was set on the Quote
      // page (step 3) and never touched again. That value doesn't reflect what the
      // customer actually paid at checkout - e.g. Payment.tsx separately computes a
      // "paymentAmount" for the card gateway that is ONLY the credit-card portion
      // (`premiumPaymentMethod.creditCardAmount`), so if that ever ends up being what
      // Adobe reads as revenue, a customer who pays $600 via CPF MediSave and $100 via
      // credit card would be reported as a $100 purchase instead of $700 - and a customer
      // who pays 100% via CPF (creditCardAmount = 0) could look like a $0 "purchase",
      // which some AA rules are configured to skip entirely.
      //
      // Recompute the real total here from `premiumPaymentMethod`, the same source this
      // page already uses to render the "Premium Payment Method" summary above, so revenue
      // is always the FULL first-year premium (CPF/MediSave + Credit Card combined).
      const firstYearTotalPremium = isEBLTC
        ? 1 + (Number(premiumPaymentMethod?.cpfFirstYearCreditCardAmount) || 0)
        : (Number(premiumPaymentMethod?.cpfMediSaveAmount) || 0) +
          (Number(premiumPaymentMethod?.creditCardAmount) || 0);
      acquisition.insurance.premium = firstYearTotalPremium.toString();

      // QB-AA-Tracking fix: `premium` above is the combined total; also push each payment
      // method's own amount so they can be tracked/reported on individually.
      acquisition.insurance.cpfFirstYearCreditCardAmount = (
        Number(premiumPaymentMethod?.cpfFirstYearCreditCardAmount) || 0
      ).toString();
      acquisition.insurance.cpfMediSaveAmount = (
        Number(premiumPaymentMethod?.cpfMediSaveAmount) || 0
      ).toString();
      acquisition.insurance.creditCardAmount = (
        Number(premiumPaymentMethod?.creditCardAmount) || 0
      ).toString();

      // QB-AA-Tracking fix: whether the customer ended up with a financial adviser.
      // Agency.tsx sets this to "USER_PREFERRED" (their own FA) or "ARISTOTLE"
      // (Singlife-assigned) when they have one, "NO_AGENT" when they decline, and it's
      // left "" for campaign types that skip the adviser step entirely - both of the
      // latter count as no adviser.
      acquisition.customer.financialAdvisorStatus = advisorSelection || "NO_AGENT";

      // QB-AA-Tracking fix: step 19 ("Thank You Page") of the journey.
      page.journeyName = MLTC_R3_JOURNEY_NAME;
      page.stepName = "Thank You Page";
      page.stepNumber = "19";

      GA.trackPageLoad({ form, acquisition, page });
      GA.trackFormCompleted({});
      // Fire unconditionally - a fully CPF-paid order is still a successful purchase even
      // though its credit-card amount is $0, so it must not be skipped here.
      GA.trackPurchaseCompleted({})
    }
  }

  useEffect(() => {
    if (!basicDetails) {
      history.push('/mltc/lets-get-started');
    }


    if (campaignDetail) {
      setCampaignCode(campaignDetail?.code);
    }

    if (!isEmpty(basicDetails)) {
      const { month, day, year } = basicDetails?.dob || { month: '', day: '', year: '' };
      const stringDate = `${month}/${day}/${year}`;
      const age = Helpers.getAge(new Date(stringDate));
      if (quoteRawResponse) {
        const quotation: SQSPocketResponse = new SQSPocketResponse(quoteRawResponse, age);
        if (summaries === undefined && quoteRawResponse) {
          let planDetails = [...quotation.mltcSummary.filter(o => !o.reviewOnly)]
          const premium = planDetails[planDetails.length - 1];
          planDetails[planDetails.length - 1] = { ...premium }
          console.log("planDetails", planDetails);
          const data = [
            { text: 'Policy number', value: policyNo },
            ...planDetails,
            // { text: 'Premium payment method', value: 'Credit Card<br>cc', fontWeight: 'bold' }
          ];
          setSummaries(data); //Not using this because the Listing component having different obj structure
          updatePlanSummary(data, quotation.firstYearDiscountEligible);
          trackOnLoad("mltc_thank_you_form");
        }
      }
      dispatch(completeStep(true));
    }
    dispatch(updateSQSQuotation(null));
    dispatch(updatePlanSelection(null));
    Helpers.clearCached();

  }, []);


  return (
    <Container>
      <FormContainer margin={"150px auto 0 auto"}>
        <FormHeaderContainer>
          <FormHeaderTitleContainer marginHorizontal={"130px"}>
            <Text align={"center"} theme={themeDefault} className={HeaderTitleCss}>
              Thank you!
            </Text>
          </FormHeaderTitleContainer>
          <FormHeaderDescriptionContainer>
            <Text theme={themeDefault} className={HeaderDescriptionCss}>Your application has been {screenSize < 768 ? <br /> : ""} successfully submitted. <br />We’ll notify you on the status of your application within 5 working days.</Text>
          </FormHeaderDescriptionContainer>
        </FormHeaderContainer>

      </FormContainer>
      <FormContentContainer>
        {agentDetailResponseRaw && <AdvisorMessageBox>        
          {screenSize < 792 && <Text theme={themeDefault} className={MessageBoxTitleCss}>Please Note</Text>}
          <Text theme={themeDefault} className={MessageBoxParagraphCss}>{mask(agentDetailResponseRaw?.fullName)} will be assigned to you as your financial advisor</Text>
        </AdvisorMessageBox>}
      </FormContentContainer>

      {basicDetails && <ListingContainer margin={'0'}>
        <Text className={ListingTitleCss}>Plan Summary</Text>
        <Listing 
          sources={planSummary}  
          variant='alternate' 
          underlineColor="#F5F5F5" 
          margin="" 
          headingColor="#444444"
          showDisclaimer={showDisclaimer}
          disclaimerText={disclaimerText} 
          disclaimerVariant={"top-divider"}
        />
      </ListingContainer>}

      <FormButtonContainer>
        <Button onClick={goToMainWebsite} width={"300px"} variant={'default'} theme={themeDefault} label={"Back to home"}></Button>
      </FormButtonContainer>

      <ContactUs margin={"184px auto 50px auto"} />
    </Container>
  )
}

export default ThankYou


const labelCss = css`
  font-size: 16px;
  font-weight: 600;
  color: #434343;
`;

const labelSubCss = css`
  font-size: 16px;
  font-weight: 600;
  color: #434343;
  margin-top: 1.5rem;
`;

const Container = styled.div`
  position:relative;
  margin: 0 auto;
  flex-direction:column;
  display: flex;
  flex-wrap: wrap;
  @media screen and (min-width: 768px) {
    max-width: 588px;
  }
  @media screen and (min-width: 1024px) {
    max-width: 792px;
  }
`;



const HeaderTitleCss = css`
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 28px;
  line-height: 140%;
  color: #434343;
  @media screen and (max-width: 768px) {
    font-size: 20px;
  }
`;


const FormContainer = styled.div<FormContainerProps>`
  flex-direction:column;
  display: flex;
  flex-wrap: wrap;
  justify-content:center;
  margin: 140px auto 0 auto;
  @media screen and (min-width: 768px) {
    margin: ${props => props.margin ? props.margin : "150px auto 0 auto"};
  }
`;
const ListingContainer = styled.div<FormContainerProps>`
  width: 100%;
  margin: 0 auto;
  max-width: 327px;
  @media screen and (min-width: 768px) {
    max-width: 588px;
    margin: ${props => props.margin ? props.margin : "150px auto 0 auto"};
  }
  @media screen and (min-width: 1024px) {
    max-width: 792px;
  }
`;
const FormHeaderContainer = styled.div`
  display: block;
  margin-bottom:48px;
  max-width:327px;
  @media screen and (min-width: 768px) {
    max-width: unset; 
  }
`;
const FormContentContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  flex-direction: column;
  column-gap: 30px;
  margin-bottom:30px;
  @media screen and (max-width: 768px) {
    flex-direction:column;
  }
`;

const FormHeaderTitleContainer = styled.div<FormHeaderTitleContainerProps>`
  display: block;
  margin: 0 ${props => props.marginHorizontal};
  margin-bottom:16px;
  @media screen and (max-width: 768px) {
    padding: 0 20px;
    margin: 0 20px 16px 20px;
  }
`;

const FormHeaderDescriptionContainer = styled.div`
  display: block;
  margin: 0 50px;
  @media screen and (max-width: 768px) {
    margin: 0;
    padding: 0;
  }
`;

const FormButtonContainer = styled.div`
  display:flex;
  justify-content:center;
  margin-top:80px;
  column-gap:40px;
  @media screen and (max-width: 768px) {
    column-gap:0;
    row-gap:10px;
    margin:40px auto 0;
    flex-direction:column;
    align-items: center;
    max-width: 327px;
    width:100%;
  }

`;

const HeaderDescriptionCss = css`
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  color: #8A8988;
  @media screen and (min-width: 768px) {
    font-weight: 600;
  }
`;

const MessageBoxTitleCss = css`
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  color: #434343;
`;
const MessageBoxParagraphCss = css`
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 180%;
  color: #434343;
  @media screen and (min-width: 768px) {
    font-weight: 400;
    line-height: 140%;
    font-size: 16px;
  }
`;

const ListingTitleCss = css`
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 28px;
  line-height: 140%;
  color: #883E89;
`;

export interface FormContainerProps {
  margin?: string;
}

export interface FormHeaderTitleContainerProps {
  marginHorizontal: string;
}
